import { Paper } from "@mui/material";
import BrandConfigurationReportHead from "./BrandConfigurationReportHead";
import BrandConfigurationReportTable from "./BrandConfigurationReportTable/BrandConfigurationReportTable";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setBrandConfigReportLoader,
  setIsFetchedBrandConfigReport,
  setPage,
  setSelectedAffiliatedSystem,
  setSelectedBrandName,
  setSelectedIntegrationStatus,
  setSelectedReason,
  setSelectedStuckAt,
} from "../../../../features/brandConfigurationReportSlice";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { BrandConfigReportEndPointLogic } from "../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import BrandConfigReportSkeleton from "../BrandSkeletons/BrandConfigReportSkeleton";
import { useSearchParams } from "react-router-dom";
import { setSelectedWebsite } from "../../../../features/websiteActionSlice";

const BrandConfigurationReport = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const website_shared_uid = "test";
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isFetchedBrandConfigReport } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );

  const getBrandConfigurationReportList = async () => {
    dispatch(setBrandConfigReportLoader(true));
    dispatch(setIsFetchedBrandConfigReport(false));
    try {
      await Promise.all([
        BrandConfigReportEndPointLogic.getBrandConfigPIECharts(
          oauthTokenHeader,
          website_shared_uid,
        ),
        BrandConfigReportEndPointLogic.getBrandConfigurationReport(
          oauthTokenHeader,
        ),
      ]);
    } catch (err) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch brand configuration report. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandConfigReportLoader(false));
      dispatch(setIsFetchedBrandConfigReport(true));
    }
  };

  // Helper function to parse filters from URL
  const parseFiltersFromURL = (filtersParam: string) => {
    const filters: Record<string, string[]> = {};
    const filterPairs = filtersParam.split("&");

    filterPairs.forEach((pair) => {
      const [key, value] = pair.split("=");
      filters[key] = decodeURIComponent(value).split(",");
    });

    return filters;
  };

  // Fetch the brand configuration report list on component mount
  useEffect(() => {
    getBrandConfigurationReportList();
    // eslint-disable-next-line
  }, []);

  //default filter set as Not Ready if no filters in URL and handle URL params
  useEffect(() => {
    const filtersParam = searchParams.get("filters");
    const searchParam = searchParams.get("search");
    const pageParam = searchParams.get("page");

    // Handle page parameter - convert to number
    if (pageParam) {
      dispatch(setPage(Number(pageParam)));
    } else {
      // Reset to page 0 if no page param exists
      dispatch(setPage(0));
    }

    if (filtersParam || searchParam) {
      // Handle search parameter
      if (searchParam) {
        dispatch(setSelectedBrandName(searchParam));
      }

      // Handle filters parameter
      if (filtersParam) {
        const filters = parseFiltersFromURL(filtersParam);

        if (filters.IntegrationStatus) {
          dispatch(setSelectedIntegrationStatus(filters.IntegrationStatus));
        }
        if (filters.StuckAt) {
          dispatch(setSelectedStuckAt(filters.StuckAt));
        }
        if (filters.Reason) {
          dispatch(setSelectedReason(filters.Reason));
        }
        if (filters.AffiliatedSystem) {
          dispatch(setSelectedAffiliatedSystem(filters.AffiliatedSystem));
        }
        if (filters.Websites) {
          dispatch(setSelectedWebsite(filters.Websites));
        }
      }

      // Clean up unwanted params but keep existing filters
      const updatedParams = new URLSearchParams(searchParams);
      ["brandSharedUid", "bkInterfaceSharedUid", "brandId", "step"].forEach(
        (key) => {
          if (updatedParams.has(key)) updatedParams.delete(key);
        },
      );

      // Only update if params were actually removed
      if (
        searchParams.has("brandSharedUid") ||
        searchParams.has("bkInterfaceSharedUid") ||
        searchParams.has("brandId") ||
        searchParams.has("step")
      ) {
        setSearchParams(updatedParams, { replace: true });
      }
    } else {
      // Set default filter
      dispatch(setSelectedIntegrationStatus(["Not Ready"]));
      const updatedParams = new URLSearchParams(searchParams);
      ["brandSharedUid", "bkInterfaceSharedUid", "brandId", "step"].forEach(
        (key) => {
          if (updatedParams.has(key)) updatedParams.delete(key);
        },
      );
      updatedParams.set("filters", "IntegrationStatus=Not Ready");
      updatedParams.set("page", "0");
      setSearchParams(updatedParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {isFetchedBrandConfigReport ? (
        <Paper>
          <BrandConfigurationReportHead />
          <BrandConfigurationReportTable />
        </Paper>
      ) : (
        <>
          <BrandConfigReportSkeleton />
        </>
      )}
    </>
  );
};

export default BrandConfigurationReport;
